import { body, bodySmall, colorSystem, h4, h5 } from '@luminovo/design-system';
import { alpha, Box, type SxProps, type Theme } from '@mui/material';
import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const MONOSPACE = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

export interface MarkdownRendererProps {
    markdown: string;

    /**
     * `reading`: body-size text for a page that is mostly text, such as a chat answer.
     */
    variant?: 'compact' | 'reading';

    /**
     * Takes a plain click on a link to an app path, which then stays in the tab; without it, every link opens a new tab.
     */
    onAppLink?: (path: string) => void;

    /**
     * Renders a link to an app path in place of a plain `<a>`, given `onAppLink`.
     */
    appLink?: React.ElementType<AppLinkProps>;
}

export type AppLinkProps = {
    href: string;
    onClick: React.MouseEventHandler<HTMLAnchorElement>;
    children?: React.ReactNode;
};

/**
 * Renders no raw HTML and no images, drops `javascript:` links, and opens links in a new tab, except a plain click on
 * an app path when `onAppLink` is given. `onAppLink` and `appLink` must keep their identity across renders
 * (useCallback, a module-level component), or every link remounts. Memoized, so a streaming chat parses only the text
 * that changed.
 */
export const MarkdownRenderer = React.memo(function MarkdownRenderer({
    markdown,
    variant = 'compact',
    onAppLink,
    appLink: AppLink = 'a',
}: MarkdownRendererProps) {
    const components = React.useMemo<Components>(
        () => ({
            ...COMPONENTS,
            a: ({ href, children }) =>
                onAppLink && href !== undefined && isAppPath(href) ? (
                    <AppLink
                        href={href}
                        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                            // With a modifier key, the browser opens it in a new tab or window, as any link.
                            if (!(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
                                event.preventDefault();
                                onAppLink(href);
                            }
                        }}
                    >
                        {children}
                    </AppLink>
                ) : (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                ),
        }),
        [onAppLink, AppLink],
    );
    return (
        <Box sx={variant === 'reading' ? READING : COMPACT}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {markdown}
            </ReactMarkdown>
        </Box>
    );
});

const COMPONENTS: Components = {
    // A table scrolls on its own, so a wide one doesn't widen the text around it.
    table: ({ children }) => (
        <div className="table-container">
            <table>{children}</table>
        </div>
    ),
    // No images: model output could point one at any URL and send data there just by being shown.
    img: ({ alt }) => <>{alt}</>,
};

/** One leading `/`: `//host` is another site. */
function isAppPath(href: string): boolean {
    return href.startsWith('/') && !href.startsWith('//');
}

const COMPACT: SxProps<Theme> = {
    '& h1': {
        ...h4,
        fontSize: '1.5rem',
        fontWeight: 700,
        color: colorSystem.neutral[8],
        margin: '16px 0 8px 0',
    },
    '& h2': {
        ...h4,
        fontSize: '1.3rem',
        fontWeight: 600,
        color: colorSystem.neutral[8],
        margin: '16px 0 8px 0',
    },
    '& h3': {
        ...h4,
        fontSize: '1.1rem',
        fontWeight: 600,
        color: colorSystem.neutral[8],
        margin: '16px 0 8px 0',
    },
    '& h4': {
        ...h4,
        color: colorSystem.neutral[8],
        margin: '16px 0 8px 0',
    },
    '& h5': {
        ...h5,
        color: colorSystem.neutral[8],
        margin: '16px 0 8px 0',
    },
    '& h6': {
        ...h5,
        color: colorSystem.neutral[8],
        margin: '16px 0 8px 0',
    },
    '& p': {
        ...bodySmall,
        color: colorSystem.neutral[9],
        margin: '8px 0',
    },
    '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '16px 0',
        ...bodySmall,
    },
    '& .table-container': {
        overflowX: 'auto',
        margin: '16px 0',
    },
    '& th': {
        ...bodySmall,
        backgroundColor: colorSystem.neutral[1],
        color: colorSystem.neutral[8],
        fontWeight: 600,
        padding: '12px 16px',
        textAlign: 'left',
        borderBottom: `2px solid ${colorSystem.neutral[3]}`,
    },
    '& td': {
        ...bodySmall,
        padding: '12px 16px',
        borderBottom: `1px solid ${colorSystem.neutral[2]}`,
        color: colorSystem.neutral[9],
    },
    '& tbody tr:hover': {
        backgroundColor: colorSystem.neutral[0],
    },
    '& code': {
        fontFamily: 'monospace',
        fontSize: '12px',
        backgroundColor: colorSystem.neutral[1],
        color: colorSystem.blue[6],
        padding: '2px 4px',
        borderRadius: '3px',
    },
    '& pre': {
        backgroundColor: colorSystem.neutral[1],
        padding: '16px',
        borderRadius: '4px',
        overflow: 'auto',
        margin: '16px 0',
    },
    '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
    },
    '& blockquote': {
        ...bodySmall,
        borderLeft: `4px solid ${colorSystem.neutral[3]}`,
        margin: '16px 0',
        paddingLeft: '16px',
        color: colorSystem.neutral[7],
        fontStyle: 'italic',
    },
    '& ul, & ol': {
        paddingLeft: '24px',
        margin: '8px 0',
    },
    '& li': {
        ...bodySmall,
        margin: '4px 0',
        color: colorSystem.neutral[9],
    },
    '& a': {
        ...bodySmall,
        color: colorSystem.blue[6],
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    '& hr': {
        border: 'none',
        height: '1px',
        backgroundColor: colorSystem.neutral[3],
        margin: '24px 0',
    },
};

const SHADE = alpha(colorSystem.neutral[9], 0.1);

const READING: SxProps<Theme> = {
    ...body,
    lineHeight: '24px',
    color: colorSystem.neutral[9],
    overflowWrap: 'anywhere',
    // Scroll shadows (macOS hides the scrollbar): a shade marks each edge with more table past it. The white `local`
    // layers move with the table and cover the `scroll` shade when the table is scrolled to that end.
    '& .table-container': {
        overflowX: 'auto',
        margin: '12px 0',
        background: [
            `linear-gradient(to right, ${colorSystem.neutral.white} 50%, transparent) left / 32px 100% no-repeat local`,
            `linear-gradient(to left, ${colorSystem.neutral.white} 50%, transparent) right / 32px 100% no-repeat local`,
            `linear-gradient(to right, ${SHADE}, transparent) left / 16px 100% no-repeat scroll`,
            `linear-gradient(to left, ${SHADE}, transparent) right / 16px 100% no-repeat scroll`,
        ].join(', '),
    },
    '& > :first-child': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },
    '& p, & ul, & ol, & blockquote, & pre': { margin: '0 0 12px' },
    '& ul, & ol': { paddingLeft: '20px' },
    '& li + li': { marginTop: '4px' },
    '& h1, & h2, & h3': { ...h4, fontSize: '16px', lineHeight: '24px', margin: '20px 0 8px' },
    '& h4, & h5, & h6': { ...h5, fontSize: '14px', lineHeight: '20px', margin: '16px 0 4px' },
    '& a': { color: colorSystem.primary[7], textUnderlineOffset: '2px' },
    '& code': {
        fontFamily: MONOSPACE,
        fontSize: '13px',
        padding: '1px 4px',
        borderRadius: '4px',
        background: colorSystem.neutral[1],
    },
    '& pre': {
        padding: '12px',
        borderRadius: '8px',
        background: colorSystem.neutral[1],
        overflowX: 'auto',
        '& code': { padding: 0, background: 'none' },
    },
    '& blockquote': {
        paddingLeft: '12px',
        borderLeft: `1px solid ${colorSystem.neutral[3]}`,
        color: colorSystem.neutral[8],
    },
    '& table': { borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums', lineHeight: '20px' },
    // The root's overflowWrap: anywhere would shrink cells mid-word; cells and headers wrap between words, so three
    // columns fit the Lumi Agent's panel, and a wider table scrolls.
    '& th, & td': {
        padding: '6px 12px',
        border: `1px solid ${colorSystem.neutral[2]}`,
        textAlign: 'left',
        verticalAlign: 'top',
        overflowWrap: 'normal',
    },
    // neutral[1] on white, but translucent, so the edge shade shows through the header too.
    '& th': { ...h5, fontSize: '13px', background: alpha(colorSystem.neutral[2], 0.46) },
    '& hr': { border: 0, borderTop: `1px solid ${colorSystem.neutral[2]}`, margin: '16px 0' },
};
